import { OktaAuth } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import { configureStore } from '@reduxjs/toolkit';
import { render as rtlRender } from '@testing-library/react';
import {
  RenderHookOptions,
  RenderHookResult,
  renderHook as rtlRenderHook
} from '@testing-library/react-hooks/dom';
import { theme } from 'cdk-radial';
import { IntlProvider } from 'react-intl';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { ROLES, Tabs } from 'common/constants';
import { ClientProvider } from 'components/ClientProvider';

import { mockClient } from 'hooks/fixtures';
import { loadMessages } from 'i18n';
import { checkWritingResponse } from 'mocks/mockData';
import { printChecksSelectedData } from 'mocks/mockData/printChecksTableData';
import { Client } from '../adapters';
import { reducer, RootState } from '../store';
import { oktaIAMVariables, restoreOriginalUri } from './OktaUtils';
import type { RadialTheme } from '../types';
import type { CombinedState, PreloadedState } from '@reduxjs/toolkit';
import type { NoInfer } from '@reduxjs/toolkit/dist/tsHelpers';
import type { RenderOptions, RenderResult } from '@testing-library/react';
import type { ReactElement, ReactNode } from 'react';

const queryClient = new QueryClient();

export const RenderWrapper = ({
  children,
  preloadedState
}: {
  children: ReactNode;
  preloadedState?: PreloadedState<CombinedState<NoInfer<RootState>>>;
}): React.JSX.Element => {
  const { locale, defaultLocale } = Client;
  const messages = loadMessages(locale);
  const store = configureStore({ reducer, preloadedState });
  const jwtFlag = true;
  return (
    <BrowserRouter>
      <Security
        oktaAuth={new OktaAuth(oktaIAMVariables(false))}
        restoreOriginalUri={restoreOriginalUri}
      >
        <Provider store={store}>
          <IntlProvider
            locale={locale}
            defaultLocale={defaultLocale}
            messages={messages}
          >
            <QueryClientProvider client={queryClient}>
              <ThemeProvider theme={theme as RadialTheme}>
                <ClientProvider client={mockClient} jwtFlag={jwtFlag}>
                  {children}
                </ClientProvider>
              </ThemeProvider>
            </QueryClientProvider>
          </IntlProvider>
        </Provider>
      </Security>
    </BrowserRouter>
  );
};

RenderWrapper.defaultProps = {
  preloadedState: {}
};

export const render = (
  ui: ReactElement,
  {
    preloadedState,
    ...renderOptions
  }: {
    preloadedState?: PreloadedState<CombinedState<NoInfer<RootState>>>;
  } & Omit<RenderOptions, 'wrapper'> = {}
): RenderResult =>
  rtlRender(ui, {
    wrapper: ({ children }: { children?: ReactNode }) => (
      <RenderWrapper preloadedState={preloadedState}>{children}</RenderWrapper>
    ),
    ...renderOptions
  });

export const renderHook = <TProps extends { children?: ReactNode }, TResult>(
  ui: (props: TProps) => TResult,
  {
    preloadedState,
    ...renderOptions
  }: {
    preloadedState?: PreloadedState<CombinedState<NoInfer<RootState>>>;
  } & Omit<RenderHookOptions<TProps>, 'wrapper'> = {}
): RenderHookResult<TProps, TResult> =>
  rtlRenderHook<TProps, TResult>(ui, {
    wrapper: ({ children }: TProps) => (
      <RenderWrapper preloadedState={preloadedState}>{children}</RenderWrapper>
    ),
    ...renderOptions
  });

export const preloadedState = {
  userStore: {
    selectedStoreId: {
      label: '1 - Test Store',
      value: 'S123456'
    }
  },
  header: {
    selectedTab: Tabs.PrintChecks
  },
  userData: {
    userRole: ROLES.AP_OFFICE_MANAGER,
    harnessFlags: {
      dmsAccountingApPaymentCheckWriting: true,
      'dms-accounting-intercompany-payment': true
    },
    isLoginFailed: false
  },
  printChecks: {
    paymentDate: '2023-01-01',
    selectedInterCompanyCIBAccount: {
      companyId: '1',
      accountName: 'Petty Cash',
      accountNumber: '9863'
    },
    printResponse: {
      printOkList: checkWritingResponse.printOkList,
      printFailureList: checkWritingResponse.printFailureList
    },
    printedOKChecks: [],
    selectedCIBAccount: {
      accountNumber: '9863',
      accountCompany: '1',
      accountName: 'Petty Cash',
      postingDescriptionRequired: false,
      printerName: '',
      index: -1
    },
    selectedVendors: printChecksSelectedData,
    manualCheckNoDetails: {
      dmsPrinterNo: 'A123',
      printerId: 'A10',
      archiveOnly: false,
      manualEntryCheckSequence: {
        checkNumber: '123',
        useSequenceForFutureChecks: true
      }
    }
  },
  paymentDate: {
    printChecks: { paymentDate: '2023-01-01', isValid: true },
    authorizePayment: { paymentDate: '2023-01-01' }
  }
};

export { act, fireEvent, screen, waitFor } from '@testing-library/react';
export type { RenderResult } from '@testing-library/react';
